/**
 * Drawing tools model (SPEC §12.5, TradingView parity; docs/TRADINGVIEW-REFERENCE.md §13,
 * ADR-0023). Pure data: no DOM, no GL. The chart engine (`@helm/chart` drawings) renders and edits
 * these; the web app stores them per user and **symbol root** in the synced `drawings/{root}`
 * document.
 *
 * Coordinates:
 * - `t` is a **bar time**: epoch milliseconds (integer), the `openTs` of the bar the point sits on
 *   (points past either end of the series are extrapolated by whole bars). Bars carry ms times in
 *   the TypeScript stack; ms keeps the stored value an exact JSON integer (ns would exceed 2^53).
 *   On another timeframe or bar type the point maps to the fractional bar index at that time
 *   (`anchor.ts`), so a drawing stays at the same moment on 1m, 1h, 500t or 8r charts.
 * - `p` is price in **integer ticks** (absolute, `price / tickSize`), never a float price.
 */
import type { Visibility } from "../indicators/types.ts";

export type DrawingPoint = { t: number; p: number };

export type LineStyle = "solid" | "dashed" | "dotted";

/** One Fibonacci / Gann level: ratio, colour, shown. */
export type FibLevel = { value: number; color: string; visible: boolean };

/**
 * Style of one drawing. Common fields first; tool-specific ones are optional and only read by the
 * tools that use them (defaults per tool in tools.ts).
 */
export type DrawingStyle = {
  /** Line / outline colour, `#RRGGBB` or `#RRGGBBAA`. */
  color: string;
  /** Line width in CSS px (1–4). */
  width: number;
  line: LineStyle;
  /** Background fill (`#RRGGBBAA`), null = none. */
  fill: string | null;
  extendLeft: boolean;
  extendRight: boolean;
  /** Text / label colour. */
  textColor: string;
  /** Price tag on the right axis (horizontal lines / rays, price label). */
  showPrice: boolean;
  /** Info / measurement readout (info line, trend angle, ranges, channels' middle line…). */
  showInfo: boolean;
  /** Fib / Gann: levels (ratios). */
  levels?: FibLevel[];
  /** Fib: level labels as ratios, percentages, or none. */
  fibLabels?: "values" | "percent" | "none";
  /** Fib: price next to each level label. */
  fibPrices?: boolean;
  /** Fib: swap the 0 and 1 ends. */
  reverse?: boolean;
  /** Fib retracement / extension / channel: draw the trend line between the anchors. */
  trendLine?: boolean;
  /** Arrow head at the end (path, arrow). */
  arrowEnd?: boolean;
  /** Arrow marker: pointing up or down. */
  direction?: "up" | "down";
  /** Long / short position: contracts (display only; never sends orders). */
  qty?: number;
  /** Regression trend: channel width in standard deviations. */
  deviations?: number;
  /** Middle line (parallel channel / pitchfork median). */
  middle?: boolean;
};

/** Every tool (TradingView names in tools.ts). The id is stored in documents: stable forever. */
export const DRAWING_TYPES = [
  // Lines
  "trend_line",
  "ray",
  "info_line",
  "extended_line",
  "trend_angle",
  "horizontal_line",
  "horizontal_ray",
  "vertical_line",
  "cross_line",
  "arrow",
  // Channels
  "parallel_channel",
  "regression_trend",
  "flat_top_bottom",
  "disjoint_channel",
  // Pitchforks
  "pitchfork",
  "schiff_pitchfork",
  "modified_schiff_pitchfork",
  "inside_pitchfork",
  // Fib & Gann
  "fib_retracement",
  "fib_extension",
  "fib_channel",
  "fib_time_zone",
  "fib_speed_fan",
  "fib_trend_time",
  "fib_circles",
  "fib_spiral",
  "gann_box",
  "gann_fan",
  // Forecasting & measurement
  "long_position",
  "short_position",
  "price_range",
  "date_range",
  "date_price_range",
  // Geometric shapes
  "rectangle",
  "rotated_rectangle",
  "ellipse",
  "circle",
  "triangle",
  "arc",
  "path",
  "polyline",
  "brush",
  "highlighter",
  // Annotation
  "text",
  "anchored_text",
  "note",
  "callout",
  "price_label",
  "arrow_marker",
] as const;

export type DrawingType = (typeof DRAWING_TYPES)[number];

export type Drawing = {
  /** Unique within the user's drawings (`d-` + 12 hex). */
  id: string;
  type: DrawingType;
  points: DrawingPoint[];
  style: DrawingStyle;
  /** Free text (text / note / callout; a label on lines). Printable ASCII renders exactly. */
  text: string;
  /** Object-tree name; absent = the tool name. */
  name?: string;
  locked: boolean;
  /** Hidden from the chart (object tree eye); still listed. */
  hidden: boolean;
  /** Stacking order: higher draws on top. */
  z: number;
  /** Visibility per timeframe unit (Visibility tab); a unit set to false hides the drawing there. */
  visibility?: Visibility;
  /**
   * Charts that show it: absent = every chart of the symbol root ("sync to all charts of this
   * symbol", the default); a chart key (`panel/pane`) = only that chart.
   */
  scope?: string;
  /** Anchored text: position as fractions of the price pane (not tied to time or price). */
  anchor?: { fx: number; fy: number };
  created: number;
  updated: number;
};

/** Magnet mode (TradingView weak / strong; SPEC §12.5 "snaps to OHLC within 8 px"). */
export type MagnetMode = "off" | "weak" | "strong";

/** Distance within which the weak magnet snaps (CSS px, SPEC §12.5). */
export const WEAK_MAGNET_PX = 8;

/** Most drawings kept per symbol root (document size bound). */
export const MAX_DRAWINGS_PER_ROOT = 1000;

/** Most points in a freehand stroke (brush / highlighter) or a path. */
export const MAX_POINTS = 2000;
